/**
 * The execution states the server actually writes (StrategyExecutionStatus):
 * STARTING → PASSED (running) → STOPPING → STOP, plus ERROR and ABANDONED.
 * RUNNING / FAILED / PENDING are kept only for rows written before that set existed.
 */
interface StatusMeta {
  label: string;
  /** antd Tag colour, for the desktop table. */
  tag: string;
  /** Text colour for the mobile pill. */
  color: string;
  /** The bot is up (or on its way up / down). */
  active: boolean;
}

const META: Record<string, StatusMeta> = {
  STARTING: { label: 'Starting', tag: 'blue', color: '#4C9AFF', active: true },
  PASSED: { label: 'Running', tag: 'green', color: '#0ECB81', active: true },
  RUNNING: { label: 'Running', tag: 'green', color: '#0ECB81', active: true },
  STOPPING: { label: 'Stopping', tag: 'orange', color: '#F0B90B', active: true },
  STOP: { label: 'Stopped', tag: 'default', color: '#848E9C', active: false },
  ERROR: { label: 'Error', tag: 'red', color: '#F6465D', active: false },
  FAILED: { label: 'Failed', tag: 'red', color: '#F6465D', active: false },
  ABANDONED: { label: 'Abandoned', tag: 'volcano', color: '#FF7A45', active: false },
  PENDING: { label: 'Pending', tag: 'orange', color: '#F0B90B', active: false },
};

export const executionStatusMeta = (status: string): StatusMeta =>
  META[status?.toUpperCase()] ?? { label: status, tag: 'default', color: '#848E9C', active: false };

/** Filter choices: `active` groups every live state, the rest match one status. */
export const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'PASSED', label: 'Running' },
  { value: 'STOPPING', label: 'Stopping' },
  { value: 'STOP', label: 'Stopped' },
  { value: 'ERROR', label: 'Error' },
  { value: 'ABANDONED', label: 'Abandoned' },
];

export const matchesStatusFilter = (status: string, filter: string): boolean => {
  if (filter === 'all') return true;
  if (filter === 'active') return executionStatusMeta(status).active;
  const s = status.toUpperCase();
  // Legacy RUNNING rows are what PASSED means today.
  return filter === 'PASSED' ? s === 'PASSED' || s === 'RUNNING' : s === filter;
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const dayList = (field: string): string | null => {
  if (field === '*') return 'Every day';
  if (field === '1-5') return 'Mon–Fri';
  const range = /^(\d)-(\d)$/.exec(field);
  if (range) return `${DAYS[+range[1] % 7]}–${DAYS[+range[2] % 7]}`;
  if (/^\d(,\d)*$/.test(field)) return field.split(',').map((d) => DAYS[+d % 7]).join(', ');
  return null;
};

/** "0 9 * * 1-5" → "09:00 · Mon–Fri"; null for anything that is not a plain daily time. */
export const describeCron = (cron: string): string | null => {
  const parts = cron?.trim().split(/\s+/) ?? [];
  if (parts.length !== 5) return null;
  const [m, h, dom, mon, dow] = parts;
  if (!/^\d{1,2}$/.test(m) || !/^\d{1,2}$/.test(h) || dom !== '*' || mon !== '*') return null;
  if (+m > 59 || +h > 23) return null;
  const days = dayList(dow);
  if (!days) return null;
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')} · ${days}`;
};
